'use client';

import { createContext, use, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

type TerminalModeContextType = {
  isTerminalMode: boolean;
  setTerminalMode: (enabled: boolean) => void;
};

const TerminalModeContext = createContext<TerminalModeContextType | null>(null);

const STORAGE_KEY = 'app-store-tracker-terminal-mode';

// External store backing the terminal-mode flag. Reading from localStorage
// through useSyncExternalStore lets the server snapshot stay `false` while the
// client hydrates with the stored value, avoiding an extra mount render.
const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

function getServerSnapshot(): boolean {
  return false;
}

function writeTerminalMode(enabled: boolean) {
  localStorage.setItem(STORAGE_KEY, String(enabled));
  for (const listener of listeners) listener();
}

export function TerminalModeProvider({ children }: { children: React.ReactNode }) {
  const isTerminalMode = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Apply/remove the .terminal CSS class on the root element so that all
  // CSS variable overrides (--accent-primary, --accent-glow, etc.) take effect.
  useEffect(() => {
    const root = document.documentElement;
    if (isTerminalMode) {
      root.classList.add('terminal');
    } else {
      root.classList.remove('terminal');
    }
  }, [isTerminalMode]);

  const setTerminalModeHandler = useCallback((enabled: boolean) => {
    writeTerminalMode(enabled);
  }, []);

  const value = useMemo(
    () => ({ isTerminalMode, setTerminalMode: setTerminalModeHandler }),
    [isTerminalMode, setTerminalModeHandler]
  );

  return (
    <TerminalModeContext.Provider value={value}>
      {children}
    </TerminalModeContext.Provider>
  );
}

export function useTerminalModeContext() {
  return use(TerminalModeContext);
}

export { TerminalModeContext };
